import React, { useState } from 'react';
import {
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Snackbar,
  IconButton
} from '@mui/material';
import {
  Share as ShareIcon,
  ContentCopy as CopyIcon,
  Email as EmailIcon,
  WhatsApp as WhatsAppIcon
} from '@mui/icons-material';

/**
 * ShareMenu — przycisk share z menu na desktopie i native share na mobile.
 *
 * Props:
 *   url      — pełny URL do udostępnienia
 *   title    — tytuł do share (opcjonalny)
 *   text     — tekst do share (opcjonalny)
 *   iconButtonSx — dodatkowe style na IconButton (opcjonalne)
 *   iconSx       — dodatkowe style na ShareIcon (opcjonalne)
 */
const ShareMenu = ({ url, title = '', text = '', iconButtonSx = {}, iconSx = {} }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const open = Boolean(anchorEl);

  const handleClick = async (e) => {
    // Na mobile — natywny share sheet
    if (navigator.share) {
      try {
        await navigator.share({ title, text: text || title, url });
      } catch (err) {
        // AbortError = użytkownik anulował — ignoruj
      }
      return;
    }
    // Desktop — pokaż menu
    setAnchorEl(e.currentTarget);
  };

  const handleClose = () => setAnchorEl(null);

  const handleCopyLink = async () => {
    handleClose();
    try {
      await navigator.clipboard.writeText(url);
      setSnackbarOpen(true);
    } catch { /* ignore */ }
  };

  const handleWhatsApp = () => {
    handleClose();
    const waUrl = `https://wa.me/?text=${encodeURIComponent(`${title}\n${url}`)}`;
    window.open(waUrl, '_blank', 'noopener');
  };

  const handleEmail = () => {
    handleClose();
    const subject = encodeURIComponent(title || 'Zobacz ogłoszenie');
    const body = encodeURIComponent(`${text || title}\n\n${url}`);
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
  };

  return (
    <>
      <IconButton onClick={handleClick} sx={iconButtonSx}>
        <ShareIcon sx={iconSx} />
      </IconButton>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{
          paper: {
            sx: { borderRadius: 2, minWidth: 200, boxShadow: '0 4px 20px rgba(0,0,0,0.15)' }
          }
        }}
      >
        <MenuItem onClick={handleCopyLink}>
          <ListItemIcon><CopyIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Kopiuj link</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleWhatsApp}>
          <ListItemIcon><WhatsAppIcon fontSize="small" sx={{ color: '#25D366' }} /></ListItemIcon>
          <ListItemText>WhatsApp</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleEmail}>
          <ListItemIcon><EmailIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Email</ListItemText>
        </MenuItem>
      </Menu>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={2000}
        onClose={() => setSnackbarOpen(false)}
        message="Link skopiowany do schowka"
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </>
  );
};

export default ShareMenu;
